import { isDefined } from '@sgme/fp';
import { useBatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { NumericFieldsContext } from 'components/common-form/contexts/NumericFieldsContext';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { withIncrementers } from 'components/shared/Percent/WithIncrementers';
import { WithMinMax } from 'components/shared/WithMinMax';
import { IntlKey } from 'locales';
import type { NumberFieldData, NumberFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const NumberField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [value, setValue] = useData<NumberFieldData>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const metadata = useFieldMetadata<NumberFieldMetadata>(id);
  const intl = useIntl();

  const highlightColor = useHighlightColor();
  const numericFieldContext = useContext(NumericFieldsContext);
  const {
    decimalPadding,
    isCellInactive,
    onEditingKeyDown: onKeyDown,
    isFocused,
    isSelectAll,
    lostInputBeforeEditing,
  } = useBatchNavigationContext({ decimalPadding: metadata?.decimalPadding });
  const lostInputValue = useMemo<number | undefined>(() => {
    return lostInputBeforeEditing ? Number(lostInputBeforeEditing) : undefined;
  }, [lostInputBeforeEditing]);

  const onBlur = useCallback(
    (val?: number) => {
      const newValue: Nullable<number> = val ?? null;
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    [value, setValue],
  );

  if (isDefined(value) && typeof value !== 'number') {
    throw new Error(
      `NumberField ${id} value should be a number or null rather than '${JSON.stringify(value)}' (${typeof value})`,
    );
  }
  const AmountComponent = numericFieldContext.useIncrementers
    ? withIncrementers(AmountWithUnit, { layout: numericFieldContext.incrementersLayout })
    : AmountWithUnit;
  const tooltipKey = metadata?.hasTooltip ? (`form.${metadata?.translationKey}.tooltip` as IntlKey) : undefined;
  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata?.translationKey}` as any}
      tooltipKey={tooltipKey}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <WithMinMax
        Component={AmountComponent}
        minimum={metadata?.minimum}
        maximum={metadata?.maximum}
        e2e={id}
        incrementStep={1}
        onBlur={onBlur}
        highlightColor={metadata?.isHighlighted ? highlightColor : undefined}
        placeholder={intl.formatMessage({ id: `form.${metadata?.translationKey}` as any, defaultMessage: ' ' })}
        unit={metadata?.unit}
        value={lostInputValue ?? value}
        isReadOnly={metadata?.isReadOnly}
        hasError={isFieldInvalid}
        decimalPadding={decimalPadding}
        precision={metadata?.nbDecimals}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
        className={isCellInactive ? 'pe-none' : ''}
      />
    </BlockOrientationFormGroup>
  );
};

NumberField.displayName = 'NumberField';
